import { Spinner } from 'flowbite-react';
import { Suspense, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import BlankProfile from '../../../assets/images/blank-profile.png';
import { DimmedBackground } from '../../../components/common/dimmed/DimmedBackground';
import { MainHead } from '../../../components/common/head/MainHead';
import { Icon } from '../../../components/common/icon/Icon';
import { PopupConfirmModal } from '../../../components/common/modal/popup/PopupConfirmModal';
import { UserProfileImage } from '../../../components/common/user/UserProfileImage';
import { pageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { useLayout } from '../../../hooks/screen/useLayout';
import { useGetUserProfile } from '../../../hooks/user/useGetUserProfile';
import { postMessageToRN } from '../../../utils/RNCommunicator/postMessageToRN.util';
import { MenuButton } from './components/MenuButton';
import { MenuSection } from './components/MenuSection';

export const MyInfoMainPage = () => {
  const { userProfile } = useGetUserProfile();
  const [isAppInfoVisible, setAppInfoVisible] = useState<boolean>(false);

  useLayout({
    fixedHeader: <MainHead backButton={{ isShow: false }} headerTitle={'내 정보'} />,
    fixedHeaderHeight: 40,
    fixedFooterHeight: 0,
  });
  const navigate = useNavigate();

  return (
    <Suspense
      fallback={
        <DimmedBackground isShow style={{ background: '#0000003a', backdropFilter: 'none' }}>
          <SpinnerWrapper>
            <Spinner color={'info'} size={'xl'} />
          </SpinnerWrapper>
        </DimmedBackground>
      }
    >
      <>
        <Helmet>
          <title>{pageInfo.myInfoPage.title}</title>
        </Helmet>
        <UserProfileSection onClick={() => navigate(pageInfo.myInfoEditProfilePage.path)}>
          <UserProfileBox>
            <UserProfileImage
              src={userProfile?.profileImage ? userProfile.profileImage : BlankProfile}
              alt={'user'}
              style={{ width: '50px', height: '50px', minWidth: '50px', minHeight: '50px' }}
            />
            <NickName>{userProfile?.nickname}</NickName>
          </UserProfileBox>
          <Icon name={'AngleRightDarkGray'} width={14} height={14} />
        </UserProfileSection>

        <MenuSection title={'내 트랙터 관리'}>
          <MenuButton text={'보유 트랙터 관리'} onClick={() => navigate(pageInfo.myInfoOwnTractorListPage.path)} />
          <MenuButton text={'판매 트랙터 관리'} onClick={() => navigate(pageInfo.myInfoSellTractorListPage.path)} />
        </MenuSection>

        <MenuSection title={'기타'}>
          <MenuButton text={'알람 설정'} onClick={() => navigate('/myinfo/alarm-setting')} />
          <MenuButton text={'공지사항'} onClick={() => navigate('/noti')} />
          <MenuButton text={'서비스 이용 약관'} onClick={() => navigate('/terms-of-use/list')} />
          <MenuButton
            text={'1:1 문의'}
            onClick={() => {
              if (window.ReactNativeWebView) {
                postMessageToRN({ messageKey: 'link', messageParam: 'http://pf.kakao.com/_DrLDxj/chat' });
              } else {
                window.open(
                  'http://pf.kakao.com/_DrLDxj/chat',
                  '_blank',
                  'popup=yes, width=450, height=700, top=20, left=120',
                );
              }
            }}
          />
        </MenuSection>

        <MenuSection>
          <MenuButton text={'앱 정보'} onClick={() => setAppInfoVisible(true)} />
          <MenuButton text={'로그아웃'} onClick={() => navigate('/logout')} />
        </MenuSection>
        <GoToDeleteAccountPageButtonWrapper>
          <GoToDeleteAccountPageButton onClick={() => navigate(pageInfo.deleteAccountPage.path)}>
            {'회원 탈퇴'}
          </GoToDeleteAccountPageButton>
        </GoToDeleteAccountPageButtonWrapper>

        <PopupConfirmModal
          isShow={isAppInfoVisible}
          title={'앱 정보'}
          onClose={() => setAppInfoVisible(false)}
          description={'플루바 마켓 Copyright 2023.\nAll rights reserved 용도 외 사용 금지'}
          buttonArray={[{ onClick: () => setAppInfoVisible(false), text: '확인', color: 'blue' }]}
        />
      </>
    </Suspense>
  );
};

const SpinnerWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
`;

const UserProfileSection = styled.div`
  display: flex;
  padding: 24px var(--radius-large, 16px);
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  background: var(--Light-Scheme-Monochrome-white, #fff);

  cursor: pointer;
`;

const UserProfileBox = styled.div`
  display: flex;
  align-items: center;
  gap: var(--radius-large, 16px);
  flex: 1 0 0;
`;

const NickName = styled.h5`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 13.6px */
`;

const GoToDeleteAccountPageButtonWrapper = styled.div`
  display: flex;
  width: 100%;
  padding: 16px;
  justify-content: center;
  align-items: center;
`;

const GoToDeleteAccountPageButton = styled.button`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
  text-decoration-line: underline;
`;
